import React, { useCallback, useState } from "react";
import styled from "styled-components";
import ConfirmDialog from "../../../components/common/ConfirmDialog";
import {
    SettingsContainer,
    Header,
    Card,
    ButtonRow,
    PrimaryButton,
    SecondaryButton,
    Toggle,
    Slider,
} from "./styles/shared";

const IntegrationSettings = ({ artifacts, onChange, savingArtifacts, onSave, dirty, onDiscard }) => {
    const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
    const items = [
        {
            key: "discordEnabled",
            title: "Discord Activity",
            desc: "Display messages, files, and collaboration activity from Discord channels.",
            icon: "/images/Discord-Logo.svg",
        },
        {
            key: "pullRequestsEnabled",
            title: "Pull Requests Visibility",
            desc: "Enable real-time code review and PR status updates across dashboards.",
            icon: "/images/github.svg",
        },
        {
            key: "googleDocsEnabled",
            title: "Google Docs Integration",
            desc: "Sync and embed documents from Google Workspace.",
            icon: "/images/google-doc.png",
        },
        {
            key: "figmaEnabled",
            title: "Figma Live Previews",
            desc: "Sync live design mockups and prototype links.",
            icon: "/images/figma.png",
        },
        {
            key: "fileEnabled",
            title: "File Visibility",
            desc: "Enable file visibility in activity cards.",
            icon: "/images/file_doc.svg",
        }
    ];

    const handleDiscardClick = useCallback(() => {
        if (!dirty) return;
        setShowDiscardConfirm(true);
    }, [dirty]);

    return (
        <SettingsContainer>
            <Header>
                <div>
                    <h2>Artifact visibility</h2>
                    <p> Control which artifact types appear in activity cards for this course.</p>
                </div>
            </Header>
            <Card>
                <List>
                    {items.map((item) => (
                        <Row key={item.key}>
                            <Left>
                                <IconBox>
                                    <IconImage src={item.icon} alt={item.title} />
                                </IconBox>

                                <TextBlock>
                                    <Title>{item.title}</Title>
                                    <Desc>{item.desc}</Desc>
                                </TextBlock>
                            </Left>

                            <Toggle>
                                <input
                                    type="checkbox"
                                    checked={artifacts[item.key]}
                                    onChange={(e) =>
                                        onChange(item.key, e.target.checked)
                                    }
                                />
                                <Slider />
                            </Toggle>
                        </Row>
                    ))}
                </List>
                <ButtonRow>
                    <SecondaryButton
                        type="button"
                        variant="secondary"
                        onClick={handleDiscardClick}
                        disabled={!dirty || savingArtifacts}
                    >
                        Discard changes
                    </SecondaryButton>
                    <PrimaryButton
                        type="button"
                        onClick={onSave}
                        disabled={savingArtifacts || !dirty}
                    >
                        {savingArtifacts ? "Saving..." : "Save changes"}
                    </PrimaryButton>
                </ButtonRow>
            </Card>

            {showDiscardConfirm && (
                <ConfirmDialog
                    title="Discard Changes?"
                    message="Discard unsaved integration changes?"
                    confirmText="Yes, Discard"
                    cancelText="No, Go Back"
                    variant="primary"
                    onConfirm={() => { setShowDiscardConfirm(false); onDiscard?.(); }}
                    onCancel={() => setShowDiscardConfirm(false)}
                />
            )}
        </SettingsContainer>
    );
};

export default IntegrationSettings;

const List = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const Row = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    border-radius: 14px;
    border: 1px solid #e5e7eb;
    background: #ffffff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
`;

const Left = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;

const IconBox = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const TextBlock = styled.div`
    display: flex;
    flex-direction: column;
`;

const Title = styled.p`
    font-size: 14px;
    font-weight: 700;
    margin: 0;
    color: #111827;
    text-align: left;
`;

const Desc = styled.p`
    font-size: 12px;
    color: #6b7280;
    margin: 4px 0 0 0;
    text-align: left;
`;

const IconImage = styled.img`
    width: 20px;
    height: 20px;
    object-fit: contain;
`;